import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Power, PowerOff, Filter } from 'lucide-react';
import { brandService } from '../services/brandService';
import { BrandResponseDTO, BrandFilters } from '../types/brand';
import { PageResponse } from '../types/api';
import { alertService } from '../services/alertService';

const BrandListPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [brands, setBrands] = useState<BrandResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filtros
  const [filters, setFilters] = useState<BrandFilters>({
    nome: '',
    active: undefined
  });

  // Carregar marcas
  const loadBrands = async () => {
    try {
      setLoading(true);
      const response = await brandService.list({
        ...filters,
        page: currentPage,
        size: pageSize,
        sort: 'nome,asc'
      });

      if (response.success && response.data) {
        setBrands(response.data.content);
        setTotalElements(response.data.totalElements);
      } else {
        alertService.error('Erro ao carregar marcas');
      }
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
      alertService.error('Erro ao carregar marcas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBrands();
  }, [currentPage, pageSize, filters]);

  // Aplicar filtros
  const handleFilter = () => {
    setCurrentPage(0);
    loadBrands();
  };

  // Limpar filtros
  const clearFilters = () => {
    setFilters({
      nome: '',
      active: undefined
    });
    setCurrentPage(0);
  };

  // Deletar marca
  const handleDelete = async (id: string, nome: string) => {
    if (window.confirm(`Tem certeza que deseja excluir a marca "${nome}"?`)) {
      try {
        const response = await brandService.delete(id);
        if (response.success) {
          alertService.success('Marca excluída com sucesso');
          loadBrands();
        } else {
          alertService.error('Erro ao excluir marca');
        }
      } catch (error) {
        console.error('Erro ao excluir marca:', error);
        alertService.error('Erro ao excluir marca');
      }
    }
  };

  // Alternar status da marca
  const handleToggleStatus = async (id: string, nome: string, active: boolean) => {
    const action = active ? 'desativar' : 'ativar';
    const result = await alertService.confirm(
      `Tem certeza que deseja ${action} a marca "${nome}"?`,
      'Esta ação pode ser revertida posteriormente.',
      `Sim, ${action}`,
      'Cancelar'
    );

    if (result.isConfirmed) {
      try {
        const response = active 
          ? await brandService.deactivate(id)
          : await brandService.activate(id);
        
        if (response.success) {
          alertService.success(`Marca ${active ? 'desativada' : 'ativada'} com sucesso`);
          loadBrands();
        } else {
          alertService.error(`Erro ao ${action} marca`);
        }
      } catch (error) {
        console.error(`Erro ao ${action} marca:`, error);
        alertService.error(`Erro ao ${action} marca`);
      }
    }
  };

  // Calcular total de páginas
  const totalPages = Math.ceil(totalElements / pageSize);

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marcas</h1>
          <p className="text-gray-600">Gerencie as marcas dos produtos</p>
        </div>
        <button
          onClick={() => navigate('/marcas/novo')}
          className="bg-primary-600 text-white px-4 py-2 rounded-berry hover:bg-primary-700 flex items-center gap-2"
        >
          <Plus size={20} />
          Nova Marca
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-berry shadow-berry border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <Filter size={20} />
            Filtros
          </button>
        </div>
        
        {showFilters && (
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  value={filters.nome || ''}
                  onChange={(e) => setFilters({ ...filters, nome: e.target.value })}
                  placeholder="Buscar por nome..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.active === undefined ? '' : filters.active.toString()}
                  onChange={(e) => setFilters({ 
                    ...filters, 
                    active: e.target.value === '' ? undefined : e.target.value === 'true' 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Todos</option>
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              </div>
              
              <div className="flex items-end gap-2">
                <button
                  onClick={handleFilter}
                  className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 flex items-center gap-2"
                >
                  <Search size={16} />
                  Buscar
                </button>
                <button
                  onClick={clearFilters}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                >
                  Limpar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-berry shadow-berry border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                    Carregando...
                  </td>
                </tr>
              ) : brands.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                    Nenhuma marca encontrada
                  </td>
                </tr>
              ) : (
                brands.map((brand) => (
                  <tr key={brand.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {brand.nome}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {brand.descricao || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        brand.ativo 
                          ? 'bg-success-100 text-success-800' 
                          : 'bg-error-100 text-error-800'
                      }`}>
                        {brand.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => navigate(`/marcas/${brand.id}`)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(brand.id, brand.nome, brand.ativo)}
                          className={`${
                            brand.ativo 
                              ? 'text-warning-600 hover:text-warning-900' 
                              : 'text-success-600 hover:text-success-900'
                          }`}
                          title={brand.ativo ? 'Desativar' : 'Ativar'}
                        >
                          {brand.ativo ? <PowerOff size={16} /> : <Power size={16} />}
                        </button>
                        <button
                          onClick={() => handleDelete(brand.id, brand.nome)}
                          className="text-error-600 hover:text-error-900"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalElements > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {currentPage * pageSize + 1} a {Math.min((currentPage + 1) * pageSize, totalElements)} de {totalElements} registros
            </div>
            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(0);
                }}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm">
                Página {currentPage + 1} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandListPage;